import React, { createContext, useContext, useRef } from 'react';
import { TStatementBalance } from '@/services/types/WalletData';

export type TStatementCacheEntry = {
  balances: TStatementBalance[];
  pdfUrls: Record<string, string>;
  firstDate?: string;
  lastDate?: string;
};

type TStatementCache = Map<string, TStatementCacheEntry>;

type TStatementCacheContext = {
  getEntry: (key: string) => TStatementCacheEntry | undefined;
  setEntry: (key: string, entry: TStatementCacheEntry) => void;
  clearAll: () => void;
};

const StatementCacheContext = createContext<TStatementCacheContext | null>(null);

export const StatementCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cacheRef = useRef<TStatementCache>(new Map());

  const getEntry = (key: string) => cacheRef.current.get(key);

  const setEntry = (key: string, entry: TStatementCacheEntry) => {
    cacheRef.current.set(key, entry);
  };

  const clearAll = () => {
    cacheRef.current.clear();
  };

  return (
    <StatementCacheContext.Provider value={{ getEntry, setEntry, clearAll }}>
      {children}
    </StatementCacheContext.Provider>
  );
};

export const useStatementCacheContext = (): TStatementCacheContext => {
  const ctx = useContext(StatementCacheContext);
  if (!ctx) throw new Error('useStatementCacheContext must be used within StatementCacheProvider');
  return ctx;
};
